import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL, fetchJson } from './api';

function authHeaders() {
    try {
        const token = String(localStorage.getItem('monsurv_auth_token') || '').trim();
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
        return {};
    }
}

function formatUsd(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';

    // 2dp for bigger values, up to 6dp for small values.
    const abs = Math.abs(n);
    const dp = abs >= 1 ? 2 : abs >= 0.01 ? 4 : 6;

    try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: dp, maximumFractionDigits: dp }).format(n);
    } catch {
        return `$${n.toFixed(dp)}`;
    }
}

function formatPct(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    return `${(n * 100).toFixed(1)}%`;
}

function formatDateTime(value) {
    try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return String(value || '');
        return d.toLocaleString();
    } catch {
        return String(value || '');
    }
}

function formatNumber(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    try {
        return new Intl.NumberFormat('en-GB').format(n);
    } catch {
        return String(n);
    }
}

function startOfMonthIso() {
    const d = new Date();
    const s = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
    return s.toISOString().slice(0, 10);
}

function todayIso() {
    return new Date().toISOString().slice(0, 10);
}

export default function PlatformUsageAnalytics() {
    const [features, setFeatures] = useState([]);

    // Filters (draft)
    const [fromDraft, setFromDraft] = useState(startOfMonthIso());
    const [toDraft, setToDraft] = useState(todayIso());
    const [featureDraft, setFeatureDraft] = useState('');
    const [roleDraft, setRoleDraft] = useState('');
    const [projectRefDraft, setProjectRefDraft] = useState('');
    const [aiOnlyDraft, setAiOnlyDraft] = useState(false);

    // Filters (applied)
    const [filters, setFilters] = useState(() => ({
        from: startOfMonthIso(),
        to: todayIso(),
        feature_name: '',
        role: '',
        project_reference: '',
        ai_only: false
    }));

    const [summary, setSummary] = useState(null);
    const [timeseries, setTimeseries] = useState([]);

    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [total, setTotal] = useState(0);
    const [sort, setSort] = useState('occurred_at_desc');

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        async function run() {
            try {
                const res = await fetchJson(`${API_BASE_URL}/api/analytics/features`, {
                    method: 'GET',
                    headers: { ...authHeaders() }
                });
                if (cancelled) return;
                setFeatures(Array.isArray(res?.features) ? res.features : []);
            } catch {
                if (cancelled) return;
                setFeatures([]);
            }
        }
        run();
        return () => { cancelled = true; };
    }, []);

    const queryString = useMemo(() => {
        const p = new URLSearchParams();
        if (filters.from) p.set('from', filters.from);
        if (filters.to) p.set('to', filters.to);
        if (filters.feature_name) p.set('feature_name', filters.feature_name);
        if (filters.role) p.set('role', filters.role);
        if (filters.project_reference) p.set('project_reference', filters.project_reference);
        if (filters.ai_only) p.set('ai_only', '1');
        return p.toString();
    }, [filters]);

    const eventsQueryString = useMemo(() => {
        const p = new URLSearchParams(queryString);
        p.set('page', String(page));
        p.set('page_size', String(pageSize));
        p.set('sort', sort);
        return p.toString();
    }, [queryString, page, pageSize, sort]);

    async function loadAll() {
        setError('');
        setLoading(true);
        try {
            const headers = { ...authHeaders() };

            const [sumRes, tsRes, eventsRes] = await Promise.all([
                fetchJson(`${API_BASE_URL}/api/analytics/summary?${queryString}`, { method: 'GET', headers }),
                fetchJson(`${API_BASE_URL}/api/analytics/timeseries?${queryString}&interval=day`, { method: 'GET', headers }),
                fetchJson(`${API_BASE_URL}/api/analytics/events?${eventsQueryString}`, { method: 'GET', headers })
            ]);

            setSummary(sumRes || null);
            setTimeseries(Array.isArray(tsRes?.points) ? tsRes.points : []);
            setRows(Array.isArray(eventsRes?.rows) ? eventsRes.rows : []);
            setTotal(Number(eventsRes?.total || 0));
        } catch (e) {
            setError(e?.message || 'Failed to load analytics');
            setSummary(null);
            setTimeseries([]);
            setRows([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        // Load on mount and when page/sort changes (applied filters stay constant until Apply).
        void loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryString, eventsQueryString]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    const featureOptions = useMemo(() => {
        const list = Array.isArray(features) ? features : [];
        return list.map((f) => String(f.feature_name || '')).filter(Boolean);
    }, [features]);

    return (
        <div style={{ marginTop: 28, paddingTop: 18, borderTop: '2px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                <h2 style={{ margin: 0 }}>Platform &amp; AI Usage</h2>
                <div className="muted" style={{ fontSize: 13 }}>Usage and cost telemetry across platform features and AI generation.</div>
            </div>

            {/* Filters */}
            <div style={{ marginTop: 14, padding: 12, border: '1px solid var(--border)', borderRadius: 5, background: 'var(--panel)' }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end' }}>
                    <div>
                        <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>From</div>
                        <input type="date" value={fromDraft} onChange={(e) => setFromDraft(e.target.value)} />
                    </div>
                    <div>
                        <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>To</div>
                        <input type="date" value={toDraft} onChange={(e) => setToDraft(e.target.value)} />
                    </div>

                    <div>
                        <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Feature</div>
                        <select value={featureDraft} onChange={(e) => setFeatureDraft(e.target.value)}>
                            <option value="">All</option>
                            {featureOptions.map((f) => (
                                <option key={f} value={f}>{f}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Role</div>
                        <select value={roleDraft} onChange={(e) => setRoleDraft(e.target.value)}>
                            <option value="">All</option>
                            <option value="Admin">Admin</option>
                            <option value="Surveyor">Surveyor</option>
                            <option value="Funder">Funder</option>
                            <option value="Developer">Developer</option>
                        </select>
                    </div>

                    <div>
                        <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Project Reference</div>
                        <input
                            type="text"
                            placeholder="e.g. M340"
                            value={projectRefDraft}
                            onChange={(e) => setProjectRefDraft(e.target.value)}
                            style={{ width: 160 }}
                        />
                    </div>

                    <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: 6 }}>
                        <input type="checkbox" checked={aiOnlyDraft} onChange={(e) => setAiOnlyDraft(e.target.checked)} />
                        <span className="muted">AI only</span>
                    </label>

                    <div style={{ flex: '1 1 auto' }} />

                    <button
                        className="primary"
                        type="button"
                        onClick={() => {
                            setPage(1);
                            setFilters({
                                from: String(fromDraft || '').trim(),
                                to: String(toDraft || '').trim(),
                                feature_name: String(featureDraft || '').trim(),
                                role: String(roleDraft || '').trim(),
                                project_reference: String(projectRefDraft || '').trim().toUpperCase(),
                                ai_only: Boolean(aiOnlyDraft)
                            });
                        }}
                    >
                        Apply
                    </button>

                    <button
                        type="button"
                        onClick={() => {
                            const f = startOfMonthIso();
                            const t = todayIso();
                            setFromDraft(f);
                            setToDraft(t);
                            setFeatureDraft('');
                            setRoleDraft('');
                            setProjectRefDraft('');
                            setAiOnlyDraft(false);
                            setPage(1);
                            setFilters({ from: f, to: t, feature_name: '', role: '', project_reference: '', ai_only: false });
                        }}
                    >
                        Reset
                    </button>
                </div>
            </div>

            {error ? <div className="error" style={{ marginTop: 12 }}>{error}</div> : null}
            {loading ? <div style={{ marginTop: 12 }}>Loading…</div> : null}

            {/* KPI tiles */}
            <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
                <Kpi title="Total Cost (USD)" value={summary ? formatUsd(summary.total_cost) : '—'} />
                <Kpi title="AI Calls" value={summary ? formatNumber(summary.total_ai_events) : '—'} />
                <Kpi title="Total Tokens" value={summary ? formatNumber(summary.total_tokens) : '—'} />
                <Kpi title="Avg Cost / AI Call" value={summary ? formatUsd(summary.avg_cost_per_ai_event) : '—'} />
                <Kpi title="Avg Latency" value={summary && summary.avg_latency_ms != null ? `${Math.round(Number(summary.avg_latency_ms))} ms` : '—'} />
                <Kpi title="Error Rate" value={summary ? formatPct(summary.error_rate) : '—'} />
            </div>

            {/* Timeseries (simple table) */}
            <div style={{ marginTop: 18, padding: 12, border: '1px solid var(--border)', borderRadius: 5, background: 'var(--panel)' }}>
                <div style={{ fontWeight: 800, marginBottom: 8 }}>Daily totals</div>
                {timeseries.length ? (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={th}>Date</th>
                                    <th style={th}>Total Cost</th>
                                    <th style={th}>AI Events</th>
                                    <th style={th}>Events</th>
                                    <th style={th}>Tokens</th>
                                </tr>
                            </thead>
                            <tbody>
                                {timeseries.map((p) => (
                                    <tr key={String(p.date)}>
                                        <td style={td}>{String(p.date).slice(0, 10)}</td>
                                        <td style={td}>{formatUsd(p.total_cost)}</td>
                                        <td style={td}>{formatNumber(p.total_ai_events)}</td>
                                        <td style={td}>{formatNumber(p.total_events)}</td>
                                        <td style={td}>{formatNumber(p.total_tokens)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="muted">No usage in this range.</div>
                )}
            </div>

            {/* Events table */}
            <div style={{ marginTop: 18, padding: 12, border: '1px solid var(--border)', borderRadius: 5, background: 'var(--panel)' }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 800 }}>Detailed events</div>
                    <div className="spacer" />

                    <div className="muted" style={{ fontSize: 12 }}>Sort</div>
                    <select value={sort} onChange={(e) => { setPage(1); setSort(e.target.value); }}>
                        <option value="occurred_at_desc">Date (newest)</option>
                        <option value="occurred_at_asc">Date (oldest)</option>
                    </select>

                    <div className="muted" style={{ fontSize: 12 }}>Page size</div>
                    <select value={String(pageSize)} onChange={(e) => { setPage(1); setPageSize(Number(e.target.value)); }}>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                </div>

                <div style={{ marginTop: 10, overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={th}>Date/Time</th>
                                <th style={th}>Feature</th>
                                <th style={th}>Project Ref</th>
                                <th style={th}>Role</th>
                                <th style={th}>Model</th>
                                <th style={th}>Tokens</th>
                                <th style={th}>Estimated Cost</th>
                                <th style={th}>Latency (ms)</th>
                                <th style={th}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length ? rows.map((r) => (
                                <tr key={String(r.event_id)}>
                                    <td style={td}>{formatDateTime(r.occurred_at)}</td>
                                    <td style={td}>{r.feature_name || ''}</td>
                                    <td style={td}>{r.project_reference || ''}</td>
                                    <td style={td}>{r.user_role || ''}</td>
                                    <td style={td}>{r.ai_model || ''}</td>
                                    <td style={td}>{r.total_tokens != null ? formatNumber(r.total_tokens) : ''}</td>
                                    <td style={td}>{r.estimated_cost != null ? formatUsd(r.estimated_cost) : ''}</td>
                                    <td style={td}>{r.latency_ms != null ? formatNumber(r.latency_ms) : ''}</td>
                                    <td style={td}>
                                        <span style={{
                                            display: 'inline-block',
                                            padding: '2px 8px',
                                            borderRadius: 999,
                                            fontWeight: 800,
                                            fontSize: 12,
                                            background: r.success ? '#dcfce7' : '#fee2e2',
                                            color: '#111827'
                                        }}>
                                            {r.success ? 'Success' : 'Fail'}
                                        </span>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td style={td} colSpan={9}><span className="muted">No events.</span></td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div className="muted">Total: {formatNumber(total)}</div>
                    <div className="spacer" />
                    <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
                    <div className="muted">Page {page} / {totalPages}</div>
                    <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</button>
                </div>
            </div>
        </div>
    );
}

function Kpi({ title, value }) {
    return (
        <div style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 5, background: 'var(--panel)' }}>
            <div className="muted" style={{ fontSize: 12 }}>{title}</div>
            <div style={{ marginTop: 6, fontWeight: 900, fontSize: 18 }}>{value}</div>
        </div>
    );
}

const th = {
    textAlign: 'left',
    fontSize: 12,
    color: 'var(--muted)',
    borderBottom: '1px solid var(--border)',
    padding: '8px 10px'
};

const td = {
    borderBottom: '1px solid var(--border)',
    padding: '8px 10px',
    verticalAlign: 'top'
};
